import LayerControls from '@atom/components/common/map/mapTools/layerControls/LayerControls';
import MapSearchBox from '@atom/components/common/map/mapTools/mapSearchBox/MapSearchBox';
import StyleControls from '@atom/components/common/map/mapTools/styleControls/StyleControls';
import zoomInMapIcon from '@atom/components/common/svgIcons/zoomInMapIcon.svg';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Progress, Tooltip } from '@atom/mui';
import { GeoJSONPoint } from '@atom/types/geojson';
import {
  LocationLinkOption,
  LocationUpdatePayload,
  MapEditMetadata,
  MapParams,
} from '@atom/types/map';
import {
  getGeoJSONFromGoogleMapShape,
  getGoogleShapeCenter,
} from '@atom/utilities/mapUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
} from '../hooks/googleMapsStateHook';

import DrawingControls from './drawingControls/DrawingControls';
import DrawingData from './drawingControls/DrawingData';
import LocationLinkControls from './locationLinkControls/LocationLinkControls';

import classes from './mapTools.module.css';

type Props = {
  loading: boolean;
  mapStyleControl?: boolean;
  mapSearchBox?: boolean;
  layerControls?: boolean;
  drawingControls?: boolean;
  editControls?: boolean;
  mapEditMetadata?: MapEditMetadata[];
  editItem?: MapEditMetadata;
  setEditItem?: (editItem: MapEditMetadata) => void;
  onSave?: (payload: LocationUpdatePayload) => void;
  locationEditZoom?: number;
  locationLinkControls?: boolean;
  locationLinkOptions?: LocationLinkOption[];
  locationLinkControlsDisabled?: boolean;
  initialOpenEditList?: boolean;
  mapParams?: MapParams;
  updateMapParams?: (mapParams: MapParams) => void;
  onCenterMapClick?: () => void;
  searchBoxInputWidth?: string;
};

const MapTools = ({
  loading,
  mapStyleControl,
  mapSearchBox,
  layerControls,
  drawingControls,
  editControls,
  mapEditMetadata,
  editItem,
  setEditItem,
  onSave,
  locationEditZoom,
  locationLinkControls,
  locationLinkOptions,
  locationLinkControlsDisabled,
  initialOpenEditList,
  mapParams,
  updateMapParams,
  onCenterMapClick,
  searchBoxInputWidth,
}: Props) => {
  const [{ searchTerm: searchTermParam }, { navigate, updateSearchParams }] =
    useNavigation({
      searchPoint: SearchParamType.STRING,
      searchTerm: SearchParamType.STRING,
      zoom: SearchParamType.NUMBER,
      center: SearchParamType.STRING,
      ids: SearchParamType.ARRAY,
      disabledIds: SearchParamType.ARRAY,
      hoverId: SearchParamType.STRING,
      isActive: SearchParamType.BOOLEAN,
    });

  const { shape, mapStyle, drawingMode, grab, mapTypeId, traffic, poi } =
    useGoogleMapsStateValue();
  const dispatch = useGoogleMapsStateUpdater();

  const updateParams = (newParams: MapParams) => {
    if (!isNilOrEmpty(updateMapParams)) {
      updateMapParams(newParams);
    } else {
      updateSearchParams(newParams);
    }
  };

  const onSelect = ({ lat, lng }, searchTerm) => {
    updateParams({
      center: `${lat},${lng}`,
      zoom: 15,
      searchPoint: `${lat},${lng}`,
      searchTerm,
    });
  };

  const onCenter = ({ lat, lng }, zoom) => {
    updateParams({
      center: `${lat},${lng}`,
      zoom,
    });
  };

  const onClearSearch = () => {
    updateParams({
      searchPoint: undefined,
      searchTerm: undefined,
    });
  };

  const onChange = key => data => {
    dispatch({ type: 'SET', payload: { key, data } });
  };

  const onToggle = payload => () => {
    dispatch({ type: 'TOGGLE', payload });
  };

  const clearDrawing = () => {
    dispatch({ type: 'SET', payload: { key: 'shape', data: null } });

    if (!isNilOrEmpty(editItem)) {
      setEditItem(null);
      dispatch({ type: 'SET', payload: { key: 'drawingMode', data: null } });
    }
  };

  const onEditSave = () => {
    const shapeLocation = getGeoJSONFromGoogleMapShape(shape);
    const shapeCenter = getGoogleShapeCenter(shapeLocation);

    updateParams({
      center: `${shapeCenter?.lat},${shapeCenter?.lng}`,
    });

    onSave({ location: shapeLocation as GeoJSONPoint, type: editItem?.type });

    if (isNilOrEmpty(updateMapParams)) {
      navigate(location, { replace: true });
    }

    clearDrawing();
  };

  const searchTerm = isNilOrEmpty(updateMapParams)
    ? searchTermParam
    : mapParams.searchTerm;

  return (
    <>
      <div className={classes.mapToolsWrapper}>
        {loading && (
          <div className={classes.spinnerContainer}>
            <Progress size={20} thickness={2} />
          </div>
        )}
        {(mapSearchBox || editControls) && (
          <MapSearchBox
            hintText="Search Maps"
            searchTerm={searchTerm}
            onLocationChange={onSelect}
            onClearSearch={onClearSearch}
            editControls={editControls}
            mapEditMetadata={mapEditMetadata}
            onCenter={onCenter}
            onChange={onChange('drawingMode')}
            editItem={editItem}
            setEditItem={setEditItem}
            clearDrawing={clearDrawing}
            onEditSave={onEditSave}
            locationEditZoom={locationEditZoom}
            initialOpenEditList={initialOpenEditList}
            inputWidth={searchBoxInputWidth}
          />
        )}
        {locationLinkControls && (
          <LocationLinkControls
            locationLinkOptions={locationLinkOptions}
            disabled={locationLinkControlsDisabled}
          />
        )}
        {mapStyleControl && (
          <StyleControls onChange={onChange('mapStyle')} mapStyle={mapStyle} />
        )}
        {drawingControls && (
          <DrawingControls
            drawingMode={drawingMode}
            grab={grab}
            onChange={onChange('drawingMode')}
            onCursorChange={onChange('grab')}
            clearDrawing={clearDrawing}
          />
        )}
        {layerControls && (
          <LayerControls
            setMapTypeId={onChange('mapTypeId')}
            mapTypeId={mapTypeId}
            togglePointsOfInterest={onToggle('poi')}
            toggleTraffic={onToggle('traffic')}
            traffic={traffic}
            pointsOfInterest={poi}
          />
        )}
        <DrawingData editItem={editItem} />
      </div>
      {onCenterMapClick && (
        <div className={classes.centerButtonContainer}>
          <Tooltip title="Center" placement="bottom-start">
            <div className={classes.iconContainer} onClick={onCenterMapClick}>
              <img src={zoomInMapIcon} />
            </div>
          </Tooltip>
        </div>
      )}
    </>
  );
};

export default MapTools;
